import { useState } from "react";
import { X } from "@phosphor-icons/react";

const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

const weekDays = (weekStart) => {
  if (!weekStart) return DAY_LETTERS.slice(1).concat("S").map((letter, i) => ({ letter, key: i }));
  const start = new Date(`${weekStart}T12:00:00`);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { letter: DAY_LETTERS[d.getDay()], key: i };
  });
};

// One relay, drawn: seven earning days.
export const RelayTrack = ({ weekStart, dark = false, todayIndex = null }) => {
  const days = weekDays(weekStart);
  const ink = dark ? "#FFFFFF" : "var(--pr-ink)";
  const line = dark ? "rgba(255,255,255,0.28)" : "var(--pr-line)";
  const chip = dark ? "rgba(255,255,255,0.10)" : "var(--pr-bg-alt)";
  return (
    <div className="flex items-center gap-1.5 sm:gap-2" data-testid="relay-track">
      {days.map((d, i) => (
        <div key={d.key} className="flex items-center flex-1 min-w-0">
          <div
            className="w-full h-8 sm:h-9 rounded-lg flex items-center justify-center text-[11px] font-bold"
            style={{
              background: todayIndex === i ? (dark ? "rgba(47,107,60,0.25)" : "var(--pr-tint-green)") : chip,
              border: `1px solid ${todayIndex === i ? "var(--pr-green)" : line}`,
              color: ink,
              fontWeight: todayIndex === i ? 800 : 700,
            }}
            data-testid={`relay-track-day-${i}`}
          >
            {d.letter}
          </div>
          {i < days.length - 1 && (
            <div className="w-1.5 sm:w-2 h-px flex-shrink-0" style={{ borderTop: `1px dashed ${line}` }} />
          )}
        </div>
      ))}
    </div>
  );
};

const LINES = [
  ["A relay is one earning week.", "Seven days, one target, one finish line. Clear the target and the relay is cleared."],
  ["The app is where you log. Your sheet is your season record.", "Log a day here and it syncs straight to your Google Sheet. You never type into the sheet yourself."],
];

const shortDate = (iso) => (iso
  ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
  : "");

// The four containers a racer needs to tell apart: this relay, this Sprint,
// this tax period, and the full 52-week race.
const containerLines = (ctx) => {
  const tp = ctx?.tax_period || {};
  const ps = ctx?.pit_stop || {};
  const out = [LINES[0]];

  out.push(ctx?.is_sprint
    ? ["Your Sprint is four relays.", "That's the free trial run. The full Payday Relay Race is 52 weekly relays, a year, taken one relay at a time."]
    : ["Your Payday Relay Race is 52 weekly relays.", "One relay at a time, in order. You never have to look at the whole year at once."]);

  if (tp.covers) {
    out.push([`Your race segment is the current tax period: ${tp.covers}.`,
      "Segments follow the U.S. estimated-tax periods, so they're uneven on purpose. Never plain three-month quarters. Surplus and shortfall are judged inside the segment you're in, on net earnings."]);
  }
  if (ps.opens_on || ctx?.next_tax_due_on) {
    const window = ps.is_open && ps.closes_on
      ? `It's open now and closes ${shortDate(ps.closes_on)}`
      : ps.opens_on ? `The next one opens ${shortDate(ps.opens_on)}` : "";
    out.push([`A Quarterly Tax Day closes out each segment.`,
      `${window ? window + ". " : ""}That's your recalibration checkpoint, reset your weekly target${ctx?.next_tax_due_on ? `, just before estimated tax is due ${shortDate(ctx.next_tax_due_on)}` : ""}.`]);
  }

  out.push(LINES[1]);
  return out;
};

// One quiet line that tells the racer which layer they're standing on.
export const RaceContextLine = ({ ctx, color = "var(--pr-muted)" }) => {
  if (!ctx) return null;
  const tp = ctx.tax_period || {};
  const ps = ctx.pit_stop || {};
  const bits = [];
  if (tp.covers) bits.push(`Current tax period: ${tp.covers}`);
  if (ps.is_open && ps.closes_on) {
    const left = typeof ps.days_left === "number" ? ` (${ps.days_left} day${ps.days_left === 1 ? "" : "s"} left)` : "";
    bits.push(`Quarterly tax day open, closes ${shortDate(ps.closes_on)}${left}`);
  } else if (ps.opens_on) {
    const until = typeof ps.days_until === "number" ? ` · in ${ps.days_until} day${ps.days_until === 1 ? "" : "s"}` : "";
    bits.push(`Next Quarterly Tax Day: ${shortDate(ps.opens_on)}${until}`);
  }
  if (!bits.length) return null;
  return (
    <p className="text-[11px] leading-relaxed mt-1" style={{ color }} data-testid="race-context-line">
      {bits.join(" · ")}
    </p>
  );
};

// In-app teaching card: what a relay is, before anything else gets explained.
export const RelayPrimer = ({ weekStart, relayNumber, totalRelays, raceContext, forceOpen = false, onDismissed }) => {
  const [hidden, setHidden] = useState(() => {
    if (forceOpen) return false;
    try { return localStorage.getItem("pr.relay.primer.dismissed") === "1"; } catch { return false; }
  });
  if (hidden) return null;
  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem("pr.relay.primer.dismissed", "1"); } catch { /* noop */ }
    onDismissed?.();
  };
  const container = raceContext?.container || (totalRelays === 4 ? "Sprint" : "Payday Relay Race");
  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="relay-primer">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--pr-muted)" }}>
            How the race works
          </p>
          <h2 className="text-lg lg:text-xl mt-0.5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }} data-testid="relay-primer-title">
            {relayNumber && totalRelays
              ? `You're on Relay ${relayNumber} of ${totalRelays} in your ${container}, that's this week.`
              : "One relay is one week of earning."}
          </h2>
        </div>
        <button onClick={dismiss} className="p-0.5 -mt-0.5 flex-shrink-0" aria-label="Dismiss" data-testid="relay-primer-dismiss">
          <X className="w-4 h-4" style={{ color: "var(--pr-muted)" }} />
        </button>
      </div>

      <RelayTrack weekStart={weekStart} />
      <p className="text-[11px] mt-2 mb-4" style={{ color: "var(--pr-muted)" }} data-testid="relay-primer-track-note">
        Your relay week runs seven days from your own start day.
      </p>

      <dl className="space-y-3">
        {containerLines(raceContext).map(([title, body]) => (
          <div key={title}>
            <dt className="text-sm font-bold" style={{ color: "var(--pr-ink)" }}>{title}</dt>
            <dd className="text-xs leading-relaxed mt-0.5" style={{ color: "var(--pr-ink2)" }}>{body}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// Marketing variant, teaches the metaphor above the fold-ish, on cream.
export const RelayExplainerStrip = () => (
  <div className="max-w-3xl mx-auto" data-testid="relay-explainer-strip">
    <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2 text-center" style={{ color: "var(--pr-muted)" }}>
      One week at a time
    </p>
    <h2 className="text-2xl sm:text-3xl text-center mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }}>
      A relay is one earning week.
    </h2>
    <div className="grid sm:grid-cols-4 gap-4 sm:gap-6 mt-7">
      {[
        ["One relay", "Seven days, one income target, one finish line."],
        ["One Sprint", "Four relays, the free trial run of the whole system."],
        ["One race segment", "The current U.S. estimated tax period."],
        ["One race", "One year, 52 weekly relays."],
      ].map(([t, b]) => (
        <div key={t}>
          <p className="text-sm font-bold mb-1" style={{ color: "var(--pr-ink)" }}>{t}</p>
          <p className="text-sm leading-relaxed" style={{ color: "var(--pr-ink2)" }}>{b}</p>
        </div>
      ))}
    </div>
  </div>
);

export default RelayPrimer;
